import { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { readLocal, writeLocal, KEYS, TECH_NAME_EVENT } from '../utils/storage.js';
import { loadIdentity, applyIdentity, clearIdentity, firstName } from '../utils/identity.js';
import Welcome from './Welcome.jsx';
import FeedbackSheet from './FeedbackSheet.jsx';

const tabs = [
  { to: '/', label: 'Scan Tag', icon: CameraIcon, end: true },
  { to: '/troubleshoot', label: 'Troubleshoot', icon: WrenchIcon },
  { to: '/wiki', label: 'Wiki', icon: BookIcon },
  { to: '/history', label: 'History', icon: ClockIcon },
];

export default function Layout() {
  const location = useLocation();
  // Tech name is required for every field-facing page: the worker rejects
  // mutating calls without an X-Tech-Name header, and jobs must be attributed.
  // Since D-LAUNCH2 it normally comes from the Google sign-in (Cloudflare
  // Access); the typed-name NameGate is only the fallback. Admin is exempt —
  // it authenticates with the PIN, not a tech identity.
  const [techName, setTechName] = useState(() => readLocal(KEYS.techName, '').trim());
  const [techEmail, setTechEmail] = useState(() => readLocal(KEYS.techEmail, '').trim());
  // 'checking' until Access answers, then 'signed-in' or 'fallback'.
  const [identity, setIdentity] = useState('checking');
  // Feedback sheet (D-LAUNCH3) — opened from the header button.
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const sync = () => {
      setTechName(readLocal(KEYS.techName, '').trim());
      setTechEmail(readLocal(KEYS.techEmail, '').trim());
    };
    window.addEventListener(TECH_NAME_EVENT, sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener(TECH_NAME_EVENT, sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  // Ask Cloudflare Access who is signed in, once per app load. A failure is
  // not an error: it just leaves the fallback gate in charge.
  useEffect(() => {
    let alive = true;
    loadIdentity()
      .then((id) => {
        if (!alive) return;
        if (id) {
          applyIdentity(id);
          setIdentity('signed-in');
        } else {
          setIdentity('fallback');
        }
      })
      .catch(() => {
        // loadIdentity is documented to never throw, but the only path to an
        // endless "Signing you in…" is a rejected promise here — belt and
        // suspenders against an old runtime missing AbortController etc.
        if (alive) setIdentity('fallback');
      });
    return () => {
      alive = false;
    };
  }, []);

  // First-run welcome, once per signed-in tech (D-LAUNCH1). Keyed by email so
  // each tech on a shared iPad sees it once; 'local' on the typed-name fallback.
  // Derived straight from storage on every render (rather than copied into
  // its own state) so there's no one-frame flash when welcomeKey changes as
  // identity resolves; setSeenTick just forces a re-render after finishWelcome
  // writes, since the write itself doesn't trigger one.
  const welcomeKey = `${KEYS.welcomeSeenPrefix}${techEmail || 'local'}`;
  const [, setSeenTick] = useState(0);
  const welcomeSeen = readLocal(welcomeKey, '') === '1';
  function finishWelcome() {
    writeLocal(welcomeKey, '1');
    setSeenTick((t) => t + 1);
  }

  const isAdminPage = location.pathname === '/admin';
  // Nothing stored yet and Access hasn't answered: wait (max 4 s) rather than
  // flash the name gate at a tech who is about to be recognised.
  const waiting = identity === 'checking' && !techName && !isAdminPage;
  const needsName = identity !== 'checking' && !techName && !isAdminPage;
  // !!techName already implies !needsName. Not while Access is still
  // answering: the key is about to switch from 'local' to the email, and the
  // tech would see the cards twice.
  const showWelcome = identity !== 'checking' && !!techName && !isAdminPage && !welcomeSeen;

  return (
    // Exactly one screen tall, so <main> is the only thing that scrolls and the
    // header (Feedback, Help) and the tab bar stay on screen. With min-h-screen
    // the whole page scrolled and the tabs sat ~1,800px below a long page.
    // dvh tracks Safari's collapsing toolbar, less the safe-area padding body
    // already carries (index.css); min-h-0 lets <main> shrink.
    <div className="h-[calc(100dvh_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom))] flex flex-col bg-ink-900">
      {/* Header — navy bar with the FaultFinder lockup left, tech + links right */}
      <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-ink-700 bg-ink-800">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <img
            src="/assets/faultfinder-logo-v2.png"
            alt="FaultFinder"
            className="w-10 h-10 rounded-lg shrink-0"
          />
          <div className="leading-tight">
            <div className="font-display font-bold text-base">
              <span className="text-white">Fault</span><span className="text-mra-red">Finder</span>
            </div>
            <div className="text-xs">
              <span className="text-white font-semibold">Find it.</span>{' '}
              <span className="text-accent font-semibold">Solve it.</span>{' '}
              <span className="text-white font-semibold">Fix it.</span>
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1 shrink-0">
          {techName && (
            <span className="hidden sm:inline text-ink-300 text-sm mr-2" title={techEmail || undefined}>
              {firstName(techName)}
            </span>
          )}
          {techName && (
            // Icon-only below `sm`: the full-width "Feedback" pill plus Help
            // and Admin doesn't fit next to the FaultFinder wordmark (which
            // can't wrap — "FaultFinder" has no space) at a 375px phone
            // width, and the two overlapped. The label returns at `sm` and
            // up, where there's room.
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-lg bg-mra-red text-ink-950 text-sm font-semibold active:scale-[0.98] transition"
              aria-label="Send feedback"
            >
              <span aria-hidden="true">💬</span>
              <span className="hidden sm:inline">Feedback</span>
            </button>
          )}
          <Link to="/help" className="btn-ghost text-sm">Help</Link>
          <Link to="/admin" className="btn-ghost text-xs text-mra-red hover:text-mra-red">
            Admin
          </Link>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 min-h-0 overflow-y-auto bg-ink-900">
        {waiting ? <SigningIn /> : needsName ? <NameGate onSet={setTechName} /> : <Outlet />}
      </main>

      {/* Bottom tab nav (iPad-friendly, thumb-reachable) */}
      <nav data-tab-bar className="grid grid-cols-4 border-t border-ink-700 bg-ink-800">
        {tabs.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-3 gap-1 transition ${
                isActive ? 'text-mra-red' : 'text-ink-400 active:text-ink-200'
              }`
            }
          >
            <Icon className="w-6 h-6" />
            <span className="text-xs font-semibold tracking-wide uppercase">{label}</span>
          </NavLink>
        ))}
      </nav>
      {showWelcome && <Welcome name={firstName(techName)} onDone={finishWelcome} />}
      <FeedbackSheet open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}

function SigningIn() {
  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <p className="text-ink-300 text-lg" role="status">Signing you in…</p>
    </div>
  );
}

// Shown whenever no tech name is stored on a field-facing page — including the
// moment a tech clears the name field on Home. Saving reveals the app again.
function NameGate({ onSet }) {
  const [value, setValue] = useState('');
  function submit(e) {
    e.preventDefault();
    const name = value.trim();
    if (!name) return;
    // A previous person's Access email must never ride along with a freshly
    // typed name on a shared iPad (Task 5 review addendum).
    clearIdentity();
    writeLocal(KEYS.techName, name);
    window.dispatchEvent(new Event(TECH_NAME_EVENT));
    onSet(name);
  }
  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <form onSubmit={submit} className="card max-w-md w-full">
        <h1 className="text-2xl font-bold text-ink-50 mb-2">Who's on the job?</h1>
        <p className="text-ink-300 mb-5">
          Enter your name so scans and troubleshooting sessions log under you.
          Saved to this iPad — you won't be asked again.
        </p>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Your name"
          autoFocus
          className="w-full min-h-[60px] px-4 rounded-xl bg-ink-800 border border-ink-700 text-ink-50 text-lg mb-4 focus:border-mra-red outline-none"
        />
        <button type="submit" disabled={!value.trim()} className="btn-primary w-full">
          Start
        </button>
      </form>
    </div>
  );
}

function CameraIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
function WrenchIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}
function ClockIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function BookIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
